import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Question } from 'models/Question'
import { getVisibleQuestions } from 'utils/question-visibility'
import { RootState } from 'redux/store'

/**
 * Returns questions filtered by visibility rules
 */
export const useVisibleQuestions = (questions: Question[]): Question[] => {
  const orderState = useSelector((state: RootState) => state.order)

  const visibleQuestions = useMemo(() => {
    return getVisibleQuestions(questions, { order: orderState } as RootState)
  }, [questions, orderState])

  return visibleQuestions
}

/**
 * Hook that filters questions by required/optional status AND visibility rules
 */
export const useVisibleCustomQuestions = (
  questions: Question[],
  showRequired: boolean,
): Question[] => {
  const orderState = useSelector((state: RootState) => state.order)

  const visibleQuestions = useMemo(() => {
    // First filter by required/optional
    const baseFilteredQuestions = questions.filter(
      (question) => question.is_required === showRequired,
    )

    // Then apply visibility rules
    return getVisibleQuestions(baseFilteredQuestions, { order: orderState } as RootState)
  }, [questions, showRequired, orderState])

  return visibleQuestions
}

/**
 * Returns a single question's visibility status
 */
export const useQuestionVisibility = (question: Question): boolean => {
  const orderState = useSelector((state: RootState) => state.order)

  const isVisible = useMemo(() => {
    const visibleQuestions = getVisibleQuestions([question], { order: orderState } as RootState)

    return visibleQuestions.length > 0
  }, [question, orderState])

  return isVisible
}
